import { ReactNode, useCallback } from 'react';

import { AlertProps, AlertTitle, Link, Alert as MuiAlert, Stack, SxProps, Theme } from '@mui/material';
import { useLocalStorage } from 'common/hooks/useLocalStorage';
import { isExternalUrl } from 'common/utils';

import { Button } from '../Buttons/Button';

import type { AlertAction } from './types';

type Props = {
  id?: string;
  title?: string;
  variant?: AlertProps['severity'];
  description?: ReactNode;
  children?: ReactNode | undefined;
  actions?: AlertAction[];
  sx?: SxProps<Theme>;
  Icon?: ReactNode;
  dismissible?: boolean;
  /**
   * ARIA role override. Defaults to MUI's `'alert'` (assertive). Pass `'status'` for non-critical,
   * informational messages (e.g. a cooldown notice) so screen readers announce them politely.
   */
  role?: AlertProps['role'];
  /**
   * `aria-live` override. Leave undefined to inherit the politeness implied by `role`; set to
   * `'polite'` alongside `role="status"` to be explicit for assistive technologies that don't map
   * the role to a live-region politeness on their own.
   */
  'aria-live'?: React.AriaAttributes['aria-live'];
} & Pick<AlertProps, 'onClose'>;

export const Alert = ({
  id,
  variant = 'white',
  actions = [],
  title,
  description,
  children,
  onClose,
  sx,
  Icon,
  dismissible,
  role,
  'aria-live': ariaLive,
}: Props) => {
  const { data, storeData } = useLocalStorage<boolean>(`alert-${id}`);

  const handleClose = useCallback(
    (event: React.SyntheticEvent) => {
      if (dismissible) {
        storeData(true);
      }
      onClose?.(event);
    },
    [dismissible, onClose, storeData]
  );

  const isDismissed = dismissible && data;

  if (isDismissed) {
    return null;
  }

  return (
    <MuiAlert
      data-testid={`alert-${variant}`}
      variant="standard"
      severity={variant}
      sx={sx}
      icon={Icon}
      role={role}
      aria-live={ariaLive}
      onClose={dismissible ? handleClose : undefined}
      aria-labelledby={title ? `${id}-title` : undefined}
    >
      {title && <AlertTitle id={`${id}-title`}>{title}</AlertTitle>}
      {description}
      {children}
      {!!actions.length && (
        <Stack direction="row" flexWrap="wrap" rowGap={1} gap={3} mt={2}>
          {actions.map(({ label, href, onClick }, index) =>
            href ? (
              <Link
                key={index}
                component="a"
                href={href}
                target={isExternalUrl(href) ? '_blank' : undefined}
                variant="natter-text-sm"
              >
                {label}
              </Link>
            ) : (
              <Button key={index} size="medium" onClick={onClick}>
                {label}
              </Button>
            )
          )}
        </Stack>
      )}
    </MuiAlert>
  );
};
